/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { usePerformanceMonitor } from '../../hooks/usePerformanceMonitor';

interface PerformanceOverlayProps {
  visible: boolean;
  onClose: () => void;
}

const PerformanceOverlay: React.FC<PerformanceOverlayProps> = ({ visible, onClose }) => {
  const metrics = usePerformanceMonitor(visible);

  if (!visible) return null;

  const formatMemory = (bytes?: number): string => {
    if (!bytes) return 'N/A';
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFpsColor = (fps: number): string => {
    if (fps >= 55) return 'text-green-400';
    if (fps >= 30) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getFrameTimeColor = (frameTime: number): string => {
    if (frameTime <= 18) return 'text-green-400';
    if (frameTime <= 33) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="fixed top-4 left-4 bg-black/80 text-white p-4 rounded-lg text-xs font-mono z-50 backdrop-blur-sm">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-sm">Performance Monitor</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <span>FPS:</span>
          <span className={`${getFpsColor(metrics.fps)} font-bold`}>
            {metrics.fps}
          </span>
        </div>

        <div className="flex justify-between">
          <span>Frame Time:</span>
          <span className={`${getFrameTimeColor(metrics.frameTime)} font-bold`}>
            {metrics.frameTime}ms
          </span>
        </div>

        <div className="flex justify-between">
          <span>Memory:</span>
          <span>{formatMemory(metrics.memoryUsed)}</span>
        </div>

        <div className="flex justify-between">
          <span>Entities:</span>
          <span>{metrics.entityCount}</span>
        </div>

        {metrics.drawCalls !== undefined && (
          <div className="flex justify-between">
            <span>Draw Calls:</span>
            <span>{metrics.drawCalls}</span>
          </div>
        )}

        <div className="flex justify-between">
          <span>Game Speed:</span>
          <span>1x</span>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-600">
        <div className="text-xs text-gray-400">
          <div>Target: 60 FPS @ 16.67ms</div>
          <div>Optimal: &lt; 100MB Memory</div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceOverlay;